/**
 * Debug Utility
 *
 * Provides utilities for checking if the application is running in debug mode.
 * Debug mode is enabled by adding `?debug=true` to the URL query string.
 *
 * Usage:
 * - Normal mode: http://localhost:5173/ (production appearance)
 * - Debug mode: http://localhost:5173/?debug=true (shows all debug visuals)
 *
 * @example
 * import { isDebugMode } from '@utils/debug'
 *
 * if (isDebugMode()) {
 *   // Show debug visuals (wireframes, green studs, collision boxes, etc.)
 * } else {
 *   // Show clean production appearance
 * }
 */

/**
 * Check if the application is running in debug mode.
 * Debug mode is enabled by adding `?debug=true` to the URL.
 *
 * @returns {boolean} True if debug mode is enabled, false otherwise
 *
 * @example
 * // Normal mode: http://localhost:5173/
 * isDebugMode() // returns false
 *
 * // Debug mode: http://localhost:5173/?debug=true
 * isDebugMode() // returns true
 */
export function isDebugMode(): boolean {
  if (typeof window === 'undefined') {
    // SSR or Node environment - default to false
    return false
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('debug') === 'true'
}

/**
 * Check if physics should be disabled (for performance profiling).
 * Physics can be disabled by adding `?noPhysics=true` to the URL.
 *
 * Task 4.1.6: This allows measuring the performance impact of Rapier physics
 * by comparing frame times with and without physics simulation.
 *
 * @returns {boolean} True if physics should be disabled, false otherwise
 *
 * @example
 * // Normal mode: http://localhost:5173/
 * isPhysicsDisabled() // returns false
 *
 * // No physics mode: http://localhost:5173/?noPhysics=true
 * isPhysicsDisabled() // returns true
 */
export function isPhysicsDisabled(): boolean {
  if (typeof window === 'undefined') {
    // SSR or Node environment - default to false
    return false
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('noPhysics') === 'true'
}
